/**
 * What the app knows about the connection — and it distinguishes two different bad days,
 * because they need different words on the banner:
 *
 * - `offline`     the device itself has no network. Nothing will work until it does.
 * - `unreachable` the phone says it is online but our requests are not getting through
 *                 (captive portal at the mall, API down, DNS in a bad mood). This is the case
 *                 a naive `navigator.onLine` check gets wrong, leaving the user staring at a
 *                 spinner that will never resolve.
 * - `online`      requests are getting through.
 *
 * The store is a plain external store so React can read it through `useSyncExternalStore`
 * without a render loop, and so it can be tested without a DOM.
 */

export type ConnectivityStatus = 'online' | 'offline' | 'unreachable';

export interface ConnectivityStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => ConnectivityStatus;
  /** Fold an observed request outcome into the status. */
  reportOutcome: (reachable: boolean) => void;
  /** Ask the probe whether the server is answering right now. */
  check: () => Promise<ConnectivityStatus>;
  destroy: () => void;
}

export interface ConnectivityStoreOptions {
  /** Where the browser online/offline events come from. Defaults to `window`. */
  target?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  /** Defaults to `navigator`. */
  connection?: { onLine: boolean };
  /** Resolves true when the server answered. Used to recover from `unreachable`. */
  probe?: () => Promise<boolean>;
  /** How often to re-probe while stuck in `unreachable`. */
  probeIntervalMs?: number;
}

const DEFAULT_PROBE_INTERVAL_MS = 15_000;

export function createConnectivityStore(options: ConnectivityStoreOptions = {}): ConnectivityStore {
  const {
    target = typeof window === 'undefined' ? undefined : window,
    connection = typeof navigator === 'undefined' ? { onLine: true } : navigator,
    probe,
    probeIntervalMs = DEFAULT_PROBE_INTERVAL_MS,
  } = options;

  const listeners = new Set<() => void>();
  /** Set when a request failed at the network level while the device claimed to be online. */
  let observedUnreachable = false;
  let status: ConnectivityStatus = connection.onLine ? 'online' : 'offline';
  let probeTimer: ReturnType<typeof setInterval> | undefined;

  function computed(): ConnectivityStatus {
    if (!connection.onLine) return 'offline';
    return observedUnreachable ? 'unreachable' : 'online';
  }

  function publish(): void {
    const next = computed();
    if (next === status) return;
    status = next;
    for (const listener of listeners) listener();
  }

  function stopProbing(): void {
    if (probeTimer !== undefined) {
      clearInterval(probeTimer);
      probeTimer = undefined;
    }
  }

  async function runProbe(): Promise<boolean> {
    if (!probe) return true;
    try {
      return await probe();
    } catch {
      return false;
    }
  }

  function startProbing(): void {
    if (!probe || probeTimer !== undefined) return;
    probeTimer = setInterval(() => {
      void check();
    }, probeIntervalMs);
  }

  async function check(): Promise<ConnectivityStatus> {
    if (!connection.onLine) {
      observedUnreachable = false;
      stopProbing();
      publish();
      return status;
    }
    const reachable = await runProbe();
    observedUnreachable = !reachable;
    if (reachable) stopProbing();
    else startProbing();
    publish();
    return status;
  }

  function reportOutcome(reachable: boolean): void {
    observedUnreachable = !reachable;
    if (reachable) stopProbing();
    else startProbing();
    publish();
  }

  function handleOnline(): void {
    // The browser fires `online` the instant an interface comes up, which is often before
    // anything can actually be reached. Trust it, then verify.
    observedUnreachable = false;
    publish();
    void check();
  }

  function handleOffline(): void {
    observedUnreachable = false;
    stopProbing();
    publish();
  }

  target?.addEventListener('online', handleOnline);
  target?.addEventListener('offline', handleOffline);

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => status,
    reportOutcome,
    check,
    destroy() {
      stopProbing();
      listeners.clear();
      target?.removeEventListener('online', handleOnline);
      target?.removeEventListener('offline', handleOffline);
    },
  };
}

/** True when nothing the shop does will reach the server right now. */
export function isDisconnected(status: ConnectivityStatus): boolean {
  return status !== 'online';
}

/** A probe that asks the API's health endpoint whether it is alive. */
export function createHealthProbe(url: string, timeoutMs = 4_000): () => Promise<boolean> {
  return async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
    }, timeoutMs);
    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
        signal: controller.signal,
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  };
}
